<template>
    <div class="ecu-card">
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="ecu-file-input"
            @change="onFileChange"
        />

        <div v-if="previewUrl" class="ecu-preview">
            <img :src="previewUrl" alt="" />
            <button
                v-if="!uploading"
                type="button"
                class="ecu-remove"
                :title="$t('common.delete')"
                @click="remove"
            >
                <i class="fal fa-times"></i>
            </button>
        </div>

        <button
            v-else
            type="button"
            class="ecu-drop"
            :disabled="uploading"
            @click="pick"
        >
            <i class="fal fa-image"></i>
            <span class="ecu-drop-title">{{ $t('events.form.cover') }}</span>
            <span class="ecu-drop-hint">{{ $t('events.form.cover_hint') }}</span>
        </button>

        <div v-if="previewUrl || uploading" class="ecu-actions">
            <BmvButton
                variant="secondary"
                size="sm"
                icon="fal fa-upload"
                :loading="uploading"
                @click="pick"
            >
                {{ $t('events.form.cover_replace') }}
            </BmvButton>
            <BmvButton
                v-if="previewUrl && !uploading"
                variant="ghost"
                size="sm"
                icon="fal fa-trash"
                @click="remove"
            >
                {{ $t('common.delete') }}
            </BmvButton>
        </div>

        <p v-if="error" class="ecu-error">
            <i class="fal fa-exclamation-triangle"></i>
            {{ error }}
        </p>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import BmvButton from '@/components/UI/v2/BmvButton.vue'

const MAX_SIZE_MB = 5

export default {
    name: 'EventCoverUpload',
    components: { BmvButton },
    props: {
        media: { type: Object, default: null }
    },
    data() {
        return {
            uploading: false,
            localPreview: null,
            error: null
        }
    },
    computed: {
        previewUrl() {
            if (this.localPreview) return this.localPreview
            if (this.media) return this.media.base64 || this.media.url || this.media.path || null
            return null
        }
    },
    methods: {
        pick() {
            if (this.uploading) return
            this.error = null
            this.$refs.fileInput && this.$refs.fileInput.click()
        },
        remove() {
            this.localPreview = null
            this.error = null
            this.$emit('change', null)
        },
        onFileChange(event) {
            const file = event.target.files && event.target.files[0]
            if (event.target) event.target.value = ''
            if (!file) return
            if (!file.type.startsWith('image/')) {
                this.error = this.$t('events.form.cover_error_type')
                return
            }
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                this.error = this.$t('events.form.cover_error_size', { max: MAX_SIZE_MB })
                return
            }
            this.upload(file)
        },
        upload(file) {
            this.uploading = true
            this.error = null
            const reader = new FileReader()
            reader.onloadend = () => {
                const base64 = reader.result
                this.localPreview = base64
                this.$emit('change', { base64, name: file.name })
                this.uploading = false
            }
            reader.onerror = () => {
                this.error = this.$t('events.form.cover_error_upload')
                this.uploading = false
            }
            reader.readAsDataURL(file)
        }
    }
}
</script>

<style scoped>
.ecu-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ecu-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.ecu-preview {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: var(--v2-radius-md);
    overflow: hidden;
    background: var(--v2-surface-2);
    border: 1px solid var(--v2-border);
}

.ecu-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.ecu-remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 0;
    background: rgba(15, 23, 42, 0.65);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.ecu-remove:hover {
    background: rgba(15, 23, 42, 0.85);
}

.ecu-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    aspect-ratio: 16 / 9;
    padding: 20px;
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    cursor: pointer;
    transition: border-color var(--v2-transition-fast),
        background var(--v2-transition-fast);
}

.ecu-drop:hover:not(:disabled) {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.ecu-drop:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.ecu-drop i {
    font-size: 1.6rem;
}

.ecu-drop-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-text-1);
}

.ecu-drop-hint {
    font-size: 0.75rem;
    color: var(--v2-text-3);
}

.ecu-actions {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
}

.ecu-error {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 8px 12px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-danger-soft);
    color: var(--v2-danger);
    font-size: 0.8125rem;
}

.ecu-error i {
    font-size: 0.85rem;
}
</style>
